import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Convert URL-safe base64 to Uint8Array
function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - base64String.length % 4) % 4);
  const base64 = (base64String + padding)
    .replace(/-/g, '+')
    .replace(/_/g, '/');
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

// Check if push notifications are supported
export function isPushSupported() {
  return 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;
}

// Get current permission status
export function getNotificationPermission() {
  if (!isPushSupported()) return 'unsupported';
  return Notification.permission;
}

// Request notification permission
export async function requestNotificationPermission() {
  if (!isPushSupported()) {
    return { granted: false, reason: 'Push notifications not supported' };
  }
  
  const permission = await Notification.requestPermission();
  return {
    granted: permission === 'granted',
    permission,
    reason: permission === 'denied' ? 'User denied notification permission' : null
  };
}

// Subscribe to push notifications
export async function subscribeToPush() {
  if (!isPushSupported()) {
    throw new Error('Push notifications not supported');
  }
  
  // Check permission
  if (Notification.permission !== 'granted') {
    const result = await requestNotificationPermission();
    if (!result.granted) {
      throw new Error(result.reason || 'Permission not granted');
    }
  }
  
  // Get service worker registration
  const registration = await navigator.serviceWorker.ready;
  
  // Get VAPID public key from server
  const { data: vapidData } = await axios.get(`${API}/push/vapid-key`);
  const vapidPublicKey = vapidData.public_key;
  
  if (!vapidPublicKey) {
    throw new Error('VAPID public key not available');
  }
  
  // Subscribe to push
  const subscription = await registration.pushManager.subscribe({
    userVisibleOnly: true,
    applicationServerKey: urlBase64ToUint8Array(vapidPublicKey)
  });
  
  // Send subscription to server
  const subscriptionJson = subscription.toJSON();
  await axios.post(`${API}/push/subscribe`, {
    endpoint: subscriptionJson.endpoint,
    keys: subscriptionJson.keys
  }, { withCredentials: true });
  
  return subscription;
}

// Unsubscribe from push notifications
export async function unsubscribeFromPush() {
  const registration = await navigator.serviceWorker.ready;
  const subscription = await registration.pushManager.getSubscription();
  
  if (subscription) {
    await subscription.unsubscribe();
  }
  
  // Tell server to remove subscription
  await axios.delete(`${API}/push/unsubscribe`, { withCredentials: true });
  
  return true;
}

// Check if currently subscribed
export async function isSubscribedToPush() {
  if (!isPushSupported()) return false;
  
  try {
    const registration = await navigator.serviceWorker.ready;
    const subscription = await registration.pushManager.getSubscription();
    return !!subscription;
  } catch {
    return false;
  }
}

// Send test notification
export async function sendTestNotification() {
  return axios.post(`${API}/push/test`, {}, { withCredentials: true });
}

// Schedule a trip reminder
export async function scheduleReminder(vesselId, reminderTime) {
  return axios.post(`${API}/push/schedule-reminder`, {
    vessel_id: vesselId,
    reminder_time: reminderTime
  }, { withCredentials: true });
}

// Get scheduled reminders
export async function getReminders() {
  const { data } = await axios.get(`${API}/push/reminders`, { withCredentials: true });
  return data;
}
